import {updateMyTeamsFilter} from '/redux/action/myTeams.action';

import {processRangeFilter} from '../filterProcessor.util';
import {
  resetBonusOptions,
  resetFibcOptions,
  resetNewCoachesOptions,
  resetRankOptions,
} from '../filters/builders/resetModalFilters.util';
import {FilterOptionProps} from '../MyTeamsContent.types';
import {generateOnApplyMyTeamsFilters} from '../MyTeamsReport.util';
import {buildFilterAnalyticsPayload} from '../util/buildFilterAnalyticsPayload.util';
import {
  hasFiltersApplied,
  isDefaultFilters,
} from '../util/myTeamsFilterDomain.util';

interface FiltersDraft {
  rankOptions: FilterOptionProps[];
  newCoachesOptions: FilterOptionProps[];
  fibcOptions: FilterOptionProps[];
  bonusOptions: FilterOptionProps[];
  closeToNextRank: boolean;
  fqvDetails: any;
  filterRangeDetails: any;
  newCoachesSponsoredDetails: any;
}
interface BuildClearFiltersPayloadParams {
  rankOptions: FilterOptionProps[];
  newCoachesOptions: FilterOptionProps[];
  fibcOptions: FilterOptionProps[];
  bonusOptions: FilterOptionProps[];
  defaultNewCoachesKey: string;
  frontlineOnly: boolean;
}

export const buildClearFiltersPayload = ({
  rankOptions,
  newCoachesOptions,
  fibcOptions,
  bonusOptions,
  defaultNewCoachesKey,
  frontlineOnly,
}: BuildClearFiltersPayloadParams) => {
  const resetRank = resetRankOptions(rankOptions ?? []);
  const resetNewCoaches = resetNewCoachesOptions(newCoachesOptions ?? []);
  const resetFibc = resetFibcOptions(fibcOptions ?? []);
  const resetBonus = resetBonusOptions(bonusOptions ?? []);

  const defaultNewCoach = resetNewCoaches.find(opt => opt.defaultVal);
  const defaultFIBC = resetFibc.find(opt => opt.defaultVal);

  return {
    draft: {
      rankOptions: resetRank,
      newCoachesOptions: resetNewCoaches,
      fibcOptions: resetFibc,
      bonusOptions: resetBonus,
      closeToNextRank: false,
    },

    reduxPayload: {
      frontlineOnly,
      rank: [],
      fibc: 'Any',
      period: defaultNewCoachesKey,
      cabEarned: false,
      aabEarned: false,
      closeToNextRank: false,
    },

    defaultNewCoach,
    defaultFIBC,
  };
};
interface ProcessApplyFiltersParams {
  filtersDraft: FiltersDraft;
}

export const processApplyFilters = ({
  filtersDraft,
}: ProcessApplyFiltersParams) => {
  const newCoachesFilter = filtersDraft.newCoachesOptions.find(i => i.selected);

  const rankOptionsFilter = filtersDraft.rankOptions.filter(i => i.selected);

  const FIBCFilter = filtersDraft.fibcOptions.find(i => i.selected);

  const bonusFilters = filtersDraft.bonusOptions.filter(i => i.selected);

  const additionalFilters = {
    ...processRangeFilter(filtersDraft.fqvDetails, 'fqv'),
    ...processRangeFilter(filtersDraft.filterRangeDetails, 'filterRange'),
    ...processRangeFilter(
      filtersDraft.newCoachesSponsoredDetails,
      'newCoachesSponsored',
    ),
  };

  return {
    newCoachesFilter,
    rankOptionsFilter,
    FIBCFilter,
    bonusFilters,
    additionalFilters,
  };
};

interface ApplyFiltersFlowParams {
  filtersDraft: FiltersDraft;

  filtersRedux: any;

  defaultNewCoachesKey: string;
  defaultNewCoachesValue: string;

  updateFilters: (data: any) => void;
  dispatch: any;
  clearMyTeamsFilters: (key: string) => void;

  meta: {
    ffTeamsOrgSR: boolean;
    lastPaidRank: string;
    coachHighestRank: string;
    coachCurrentRank: string;
    coachId: string;
    reportName: string;
  };
}

export const applyFiltersFlow = ({
  filtersDraft,
  filtersRedux,
  defaultNewCoachesKey,
  defaultNewCoachesValue,
  updateFilters,
  dispatch,
  clearMyTeamsFilters,
  meta,
}: ApplyFiltersFlowParams) => {
  const {
    newCoachesFilter,
    rankOptionsFilter,
    FIBCFilter,
    bonusFilters,
    additionalFilters,
  } = processApplyFilters({filtersDraft});

  updateFilters({
    rankOptions: filtersDraft.rankOptions,
    newCoachesOptions: filtersDraft.newCoachesOptions,
    fibcOptions: filtersDraft.fibcOptions,
    bonusOptions: filtersDraft.bonusOptions,
    closeToNextRank: filtersDraft.closeToNextRank,
    fqvWithoutFilter: filtersDraft.fqvDetails,
    filterRangeWithoutFilter: filtersDraft.filterRangeDetails,
    newCoachesSponsoredWithoutFilter: filtersDraft.newCoachesSponsoredDetails,
  });

  const updatedFilters = {
    ...filtersRedux,
    rank: rankOptionsFilter.map(r => r.id),
    fibc: FIBCFilter?.id || 'Any',
    period: newCoachesFilter?.id || defaultNewCoachesKey,
    frontlineOnly: filtersRedux.frontlineOnly,

    cabEarned: bonusFilters.some(b => b.id === 'cabEarnedLastPeriod'),
    aabEarned: bonusFilters.some(b => b.id === 'aabEarnedLastPeriod'),

    ...additionalFilters,
    closeToNextRank: filtersDraft.closeToNextRank,
  };

  if (
    isDefaultFilters(
      updatedFilters,
      defaultNewCoachesKey,
      defaultNewCoachesValue,
    )
  ) {
    clearMyTeamsFilters(defaultNewCoachesKey);
    return;
  }

  dispatch(updateMyTeamsFilter(updatedFilters));

  if (hasFiltersApplied(updatedFilters, defaultNewCoachesKey)) {
    const payload = buildFilterAnalyticsPayload({
      updatedFilters,
      defaultNewCoachesKey,
      ffTeamsOrgSR: meta.ffTeamsOrgSR,
    });

    generateOnApplyMyTeamsFilters({
      ...payload,
      ...meta,
    });
  }
};
